/**
 * The time of day a walkable scene shows (ADR 0032, 0033): a lighting preset
 * of the scene's record. The choice is per kind of scene, because the kinds
 * have different presets: the town's two (`day`, `night`: a town and its
 * interiors) and the airship's five (`dawn`, `morning`, `afternoon`,
 * `dusk`, `night`: its deck and rooms; renamed from twilight and evening, Q51). Each kind remembers its own in
 * `localStorage` (`ab.time.town`, `ab.time.ship`, like the tier; the town's
 * falls back to the old `ab.time`), so a choice on the ship does not reset the
 * town's and the other way round. `?time=<name>` overrides both (a kind
 * without that preset shows its default); else the remembered choice; else
 * none, and each scene shows its record's default.
 */
export type TimeKind = 'town' | 'ship';
export type TimeChoice = Readonly<Record<TimeKind, string | null>>;

const name = /^[a-z0-9_]+$/;
const keyOf = (kind: TimeKind): string => `ab.time.${kind}`;

/** The kind of time a scene's record keeps (`kind` is the scene's: `town`, `interior`, `ship_deck`, `ship_room`). */
export const timeKindOf = (sceneKind: string): TimeKind => (sceneKind === 'ship_deck' || sceneKind === 'ship_room' ? 'ship' : 'town');

const saved = (key: string): string | null => {
  try {
    const v = localStorage.getItem(key);
    return v !== null && name.test(v) ? v : null;
  } catch {
    // Storage can be unavailable (a private window); each scene's default then.
    return null;
  }
};

export const chooseTime = (params: URLSearchParams): TimeChoice => {
  const asked = params.get('time');
  if (asked !== null && name.test(asked)) return { town: asked, ship: asked };
  return { town: saved(keyOf('town')) ?? saved('ab.time'), ship: saved(keyOf('ship')) };
};

/** Remembers a time the player chose in the pause menu, for its kind of scene. */
export const rememberTime = (kind: TimeKind, time: string): void => {
  try {
    localStorage.setItem(keyOf(kind), time);
  } catch {
    // Not remembered; the next visit starts at each scene's default.
  }
};
